/*
  Tailkind Quiz v2 - core logic
  Implements Quiz v2 Implementation Spec v2.1, sections A7 (config),
  A8 (computeProfile) and A9 (property schema).

  This file holds PURE logic only - no DOM, no network, no Liquid. That is
  deliberate: it lets scripts/test-quiz.js run the A10 worked examples and the
  A11 acceptance tests in node before any of it is wired to Klaviyo.

  All dates are handled in UTC. A birthday that shifts by a day because the
  browser is in BST is not a bug anyone enjoys finding later.
*/
(function (root, factory) {
  if (typeof module === 'object' && module.exports) {
    module.exports = factory();
  } else {
    root.TailkindQuiz = factory();
  }
}(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  /* ---------------------------------------------------------------- A7
     Config constants. Every tunable value lives here and nowhere else.
  ------------------------------------------------------------------- */

  var CHEWS_PER_POUCH = 60;

  // Canonical SKUs. Format: TK-SUP-<TYPE>-STD-X01.
  // The 12-month gate is confirmed for Joint Care and applied to all four.
  var SKUS = {
    joint:   { sku: 'TK-SUP-JNT-STD-X01', name: 'Joint Care',  live: true,  minAgeMonths: 12 },
    gut:     { sku: 'TK-SUP-GUT-STD-X01', name: 'Gut Health',  live: false, minAgeMonths: 12 },
    calming: { sku: 'TK-SUP-CLM-STD-X01', name: 'Calming',     live: false, minAgeMonths: 12 },
    skin:    { sku: 'TK-SUP-SKN-STD-X01', name: 'Skin & Coat', live: false, minAgeMonths: 12 }
  };

  var PRIORITY_TO_SKU = {
    mobility: 'joint', digestion: 'gut', calm: 'calming', coat: 'skin'
  };

  // Senior thresholds in months. Large dogs age faster.
  var SENIOR_MONTHS = {
    under_10kg: 120,   // 10 years
    '10_25kg':   96,   //  8 years
    over_25kg:   72,   //  6 years
    unknown:     96    //  default to mid
  };

  var CHEWS_PER_DAY = {
    under_10kg: 1, '10_25kg': 2, over_25kg: 2, unknown: 2
  };

  var PUPPY_MAX_MONTHS = 12;

  // Midpoint offset applied when age is given in whole years. "Five years"
  // means anywhere from 5.0 to 5.99, so we take the middle. Biasing early
  // costs nothing; biasing late delays the senior crossing by up to eleven
  // months, and that is the best-converting sequence in the programme.
  var APPROX_YEAR_OFFSET_MONTHS = 6;

  var QUIZ_VERSION = 'v2';
  // quiz_v2_1 -> v2_2 when the two checkboxes became one. v2_2 -> v2_3 when
  // that last checkbox went too and consent became the button press, under a
  // notice naming the marketing. That is a change of mechanism, not just of
  // wording, so the two must never be read as the same thing: a v2_2 record
  // means somebody ticked a box, a v2_3 record means somebody pressed a button
  // with the notice above it. A consent record has to be able to show what the
  // person actually saw and did, so this moves whenever either changes
  // materially - not when a typo is fixed.
  var CONSENT_WORDING_VERSION = 'quiz_v2_3';
  var DOG_NAME_MAX = 30;
  var OWNER_NAME_MAX = 30;

  // The owner's first name. Goes to Klaviyo as the standard `first_name`
  // ATTRIBUTE, not a custom property - that is what the Shopify integration
  // merges against when they later place an order, and what {{ first_name }}
  // resolves to in a template. Returns null rather than "" when absent, so
  // Klaviyo's |default: filter fires the same way dog_name's does.
  function cleanOwnerName(raw) {
    var n = (raw || '').trim();
    return n ? n.slice(0, OWNER_NAME_MAX) : null;
  }

  /* ------------------------------------------------------- health scores
     Seven 1-5 scales captured after the priority screen.

     POLARITY IS UNIFORM AND DELIBERATE: on every scale, 1 is the best
     outcome and 5 is the most concerning. Mixed polarity is the classic way
     a score set becomes unanalysable six months later, when nobody remembers
     whether a high teeth score meant clean teeth or rotten ones. It also
     means a Klaviyo segment is always ">= 4", never a per-property lookup.
  ------------------------------------------------------------------- */

  var SCORE_KEYS = ['mobility', 'teeth', 'stool', 'scooting', 'odour', 'anxiety', 'separation'];

  // Which SKU each score speaks to. Teeth maps to nothing today - captured
  // for future use, and left out of every cluster so it cannot quietly skew
  // a recommendation.
  var SCORE_CLUSTERS = {
    joint:   ['mobility'],
    gut:     ['stool', 'scooting', 'odour'],
    calming: ['anxiety', 'separation']
  };

  /* ---------------------------------------------------------- date utils
     Hand-rolled rather than pulling in date-fns - the theme carries no JS
     dependencies and this is not enough maths to justify the first one.
  ------------------------------------------------------------------- */

  function utc(y, m, d) { return new Date(Date.UTC(y, m, d)); }

  function startOfMonth(date) {
    return utc(date.getUTCFullYear(), date.getUTCMonth(), 1);
  }

  function startOfDay(date) {
    return utc(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
  }

  function addMonths(date, n) {
    return utc(date.getUTCFullYear(), date.getUTCMonth() + n, date.getUTCDate());
  }

  function subMonths(date, n) { return addMonths(date, -n); }

  // Whole months elapsed between two dates.
  function monthsBetween(from, to) {
    var months = (to.getUTCFullYear() - from.getUTCFullYear()) * 12
               + (to.getUTCMonth() - from.getUTCMonth());
    if (to.getUTCDate() < from.getUTCDate()) months -= 1;
    return months;
  }

  // Next occurrence of the DOB's month+day, on or after today.
  function nextAnniversary(dob, today) {
    var t = startOfDay(today);
    var cand = utc(t.getUTCFullYear(), dob.getUTCMonth(), dob.getUTCDate());
    if (cand < t) cand = utc(t.getUTCFullYear() + 1, dob.getUTCMonth(), dob.getUTCDate());
    return cand;
  }

  // Next occurrence of the first of the DOB's month, on or after today.
  function nextAnniversaryMonth(dob, today) {
    var t = startOfDay(today);
    var cand = utc(t.getUTCFullYear(), dob.getUTCMonth(), 1);
    if (cand < t) cand = utc(t.getUTCFullYear() + 1, dob.getUTCMonth(), 1);
    return cand;
  }

  function toISODate(date) {
    return date ? date.toISOString().slice(0, 10) : null;
  }

  /* ------------------------------------------------------- tier 2 derive
     A9: dog_dob is always stored as the first of a month.
  ------------------------------------------------------------------- */

  function deriveDob(unit, value, today) {
    var base = startOfMonth(today || new Date());
    var months = unit === 'months'
      ? value
      : (value * 12) + APPROX_YEAR_OFFSET_MONTHS;
    return subMonths(base, months);
  }

  /* ---------------------------------------------------------------- A5
     Whether to render the activity screen. Skipped for under-12-months,
     including tier-3 profiles that declared "puppy" without a date - the
     reasoning in A5 is about the owner having no useful answer, which does
     not depend on how precisely we know the age.
  ------------------------------------------------------------------- */

  function shouldAskActivity(answers, today) {
    if (answers.dobPrecision === 'unknown') {
      return answers.declaredStage !== 'puppy';
    }
    if (!answers.dob) return true;
    return monthsBetween(answers.dob, today || new Date()) >= PUPPY_MAX_MONTHS;
  }

  /* ---------------------------------------------------------------- A8
     computeProfile - the whole of the quiz's business logic.
  ------------------------------------------------------------------- */

  function computeProfile(answers, today) {
    today = today || new Date();

    var dobPrecision = answers.dobPrecision;          // exact | approx | unknown
    var dob = answers.dob || null;
    var weightBand = answers.weightBand;
    var priority = answers.priority;

    var hasDob = dobPrecision !== 'unknown' && dob != null;

    // --- age ---
    var ageMonths = hasDob ? monthsBetween(dob, today) : null;

    // --- life track ---
    var lifeTrack;
    if (!hasDob) {
      lifeTrack = answers.declaredStage;              // puppy | adult | senior
    } else if (ageMonths < PUPPY_MAX_MONTHS) {
      lifeTrack = 'puppy';
    } else if (ageMonths >= SENIOR_MONTHS[weightBand]) {
      lifeTrack = 'senior';
    } else {
      lifeTrack = 'adult';
    }

    // --- feeding ---
    var chewsPerDay = CHEWS_PER_DAY[weightBand];
    var daysSupply = CHEWS_PER_POUCH / chewsPerDay;   // 60 or 30

    // --- recommendation ---
    var target = SKUS[PRIORITY_TO_SKU[priority]];

    // --- result state ---
    var resultState;
    if (!target.live) {
      resultState = 'waitlist';
    } else if (target.minAgeMonths == null) {
      resultState = 'buyable';
    } else if (ageMonths != null && ageMonths < target.minAgeMonths) {
      resultState = 'age_deferred';
    } else if (ageMonths == null && lifeTrack === 'puppy') {
      resultState = 'age_deferred';                   // unknown DOB, declared puppy
    } else {
      resultState = 'buyable';
    }

    // --- dates (only when DOB is real) ---
    var seniorThresholdDate = hasDob
      ? addMonths(dob, SENIOR_MONTHS[weightBand]) : null;

    var eligibleFrom = (hasDob && target.minAgeMonths != null)
      ? addMonths(dob, target.minAgeMonths) : null;

    // Tier-3 declared puppies have no date, so nothing would ever fire to tell
    // them they are eligible - the profile just goes quiet. Assume the dog is
    // at the START of puppyhood today and count the SKU's gate from there.
    // Deliberately the late end of the range: telling someone their dog is old
    // enough when it is not is a product-safety error, telling them a few
    // months late is a marketing one. Flagged as assumed so the email can
    // hedge its wording the way the birthday-month email does.
    var eligibleFromAssumed = false;
    if (!hasDob && lifeTrack === 'puppy' && target.minAgeMonths != null) {
      eligibleFrom = addMonths(startOfMonth(today), target.minAgeMonths);
      eligibleFromAssumed = true;
    }

    // Birthday routing.
    //
    // A3 as written treats "exact" as month+year and still promises a send
    // "on the day", which it cannot honour - a month+year profile only ever
    // knows the 1st. So the split is by whether we actually hold a DAY, not
    // by the precision tier:
    //
    //   day known    -> next_birthday, the real anniversary. Safe to say
    //                   "today". This is the only tier that can carry an SMS.
    //   month known  -> next_birthday_month, the 1st. Hedged copy, exactly
    //                   like the approx tier already does.
    //   nothing      -> neither.
    //
    // dob_precision keeps its three documented values so nothing built on
    // A9 breaks; dob_has_day is additive and is what the flows filter on.
    var hasDay = dobPrecision === 'exact' && !!answers.dobHasDay && !!dob;

    var nextBirthday = hasDay ? nextAnniversary(dob, today) : null;
    var nextBirthdayMonth = (!hasDay && dob && (dobPrecision === 'exact' || dobPrecision === 'approx'))
      ? nextAnniversaryMonth(dob, today) : null;

    return {
      ageMonths: ageMonths,
      lifeTrack: lifeTrack,
      chewsPerDay: chewsPerDay,
      daysSupply: daysSupply,
      recommendedSku: target.sku,
      recommendedName: target.name,
      resultState: resultState,
      dobPrecision: dobPrecision,
      hasDay: hasDay,
      seniorThresholdDate: seniorThresholdDate,
      eligibleFrom: eligibleFrom,
      eligibleFromAssumed: eligibleFromAssumed,
      nextBirthday: nextBirthday,
      nextBirthdayMonth: nextBirthdayMonth
    };
  }

  /* -------------------------------------------------------- score maths
     Cluster averages, rounded to one decimal. These describe what the owner
     reported; they do NOT alter the recommendation. Screen 5 (priority) is
     still the only thing that picks a SKU, per A6. If a cluster disagrees
     loudly with the chosen priority that is useful segmentation data, not a
     reason to override what the customer explicitly asked for.
  ------------------------------------------------------------------- */

  function scoreClusters(scores) {
    if (!scores) return null;
    var out = {};
    Object.keys(SCORE_CLUSTERS).forEach(function (key) {
      var vals = SCORE_CLUSTERS[key]
        .map(function (k) { return scores[k]; })
        .filter(function (v) { return typeof v === 'number'; });
      if (!vals.length) return;
      var sum = vals.reduce(function (a, b) { return a + b; }, 0);
      out[key] = Math.round((sum / vals.length) * 10) / 10;
    });
    return Object.keys(out).length ? out : null;
  }

  // The cluster scoring highest. Ties resolve to null rather than picking
  // arbitrarily - a coin-flip "top concern" is worse than none.
  function topConcern(clusters) {
    if (!clusters) return null;
    var keys = Object.keys(clusters);
    if (!keys.length) return null;
    var best = keys[0];
    keys.forEach(function (k) { if (clusters[k] > clusters[best]) best = k; });
    var tied = keys.filter(function (k) { return clusters[k] === clusters[best]; });
    return tied.length > 1 ? null : best;
  }

  /* ---------------------------------------------------------------- A9
     Property schema. The "written when" column is enforced here: a property
     that should be absent is never assigned, so it does not reach the payload
     as "" or null. Klaviyo default filters only work on absent properties.
  ------------------------------------------------------------------- */

  function buildProperties(answers, computed, consent, now) {
    var p = {};

    // Only if given. Never an empty string. (A2)
    var name = (answers.dogName || '').trim();
    if (name) p.dog_name = name.slice(0, DOG_NAME_MAX);

    var hasDob = computed.dobPrecision !== 'unknown';

    if (hasDob) {
      // Deviation from A9, which specifies "first of month". When the owner
      // gives us the day we store the real date, because a birthday send on
      // the wrong day is the one email where being wrong is embarrassing.
      p.dog_dob = toISODate(answers.dob);
      p.dog_age_months = computed.ageMonths;
      p.senior_threshold_date = toISODate(computed.seniorThresholdDate);
      // Only written when true, so a Klaviyo "is set" filter selects exactly
      // the profiles that can carry a real birthday send.
      if (computed.hasDay) p.dob_has_day = true;
    }

    p.dob_precision = computed.dobPrecision;
    p.dog_weight_band = answers.weightBand;

    // Non-puppies only - the screen is not rendered for under-12-months. (A5)
    if (answers.activity) p.dog_activity = answers.activity;

    p.quiz_priority = answers.priority;
    p.life_track = computed.lifeTrack;
    p.quiz_result_state = computed.resultState;
    p.quiz_recommended_sku = computed.recommendedSku;

    // The customer-facing name for the same product. Written alongside the SKU
    // so an email can say "Gut Health" without every template repeating a
    // three-branch conditional to translate TK-SUP-GUT-STD-X01. The SKU stays
    // the join key for segments; this is only ever for display.
    p.quiz_recommended_name = computed.recommendedName;
    p.chews_per_day = computed.chewsPerDay;
    p.days_supply = computed.daysSupply;

    // EXACT ONLY / APPROX ONLY - never both. (A9, A11)
    if (computed.nextBirthday) p.next_birthday = toISODate(computed.nextBirthday);
    if (computed.nextBirthdayMonth) p.next_birthday_month = toISODate(computed.nextBirthdayMonth);

    if (computed.eligibleFrom) {
      p.joint_eligible_from = toISODate(computed.eligibleFrom);
      // Only written when true, so a Klaviyo filter on "is set" cleanly
      // selects the profiles whose eligibility date is a guess.
      if (computed.eligibleFromAssumed) p.joint_eligible_from_assumed = true;
    }

    // Health scores. Each written only if answered, so a half-finished set
    // does not land as zeroes - a zero would read as "perfect" on a 1-5 scale.
    if (answers.scores) {
      SCORE_KEYS.forEach(function (k) {
        var v = answers.scores[k];
        if (typeof v === 'number') p['score_' + k] = v;
      });

      var clusters = scoreClusters(answers.scores);
      if (clusters) {
        if (clusters.joint != null) p.concern_joint = clusters.joint;
        if (clusters.gut != null) p.concern_gut = clusters.gut;
        if (clusters.calming != null) p.concern_calming = clusters.calming;

        var top = topConcern(clusters);
        if (top) p.concern_top = top;

        // Flags the profiles worth a different email: what they scored
        // highest on is not what they chose to start with.
        var chosen = PRIORITY_TO_SKU[answers.priority];
        if (top && chosen && top !== chosen) p.concern_mismatch = true;
      }
    }

    p.quiz_completed_at = (now || new Date()).toISOString();
    p.quiz_version = QUIZ_VERSION;
    p.signup_source = 'quiz';
    p.consent_marketing = !!consent.marketing;
    p.consent_wording_version = CONSENT_WORDING_VERSION;

    // quiz_taken_count is deliberately NOT written here. Incrementing it
    // requires reading the existing profile, and Klaviyo's client API is
    // write-only from the browser. Count "Quiz Completed" events in Klaviyo
    // instead - it is native, and correct across devices.

    return p;
  }

  return {
    CHEWS_PER_POUCH: CHEWS_PER_POUCH,
    SKUS: SKUS,
    PRIORITY_TO_SKU: PRIORITY_TO_SKU,
    SENIOR_MONTHS: SENIOR_MONTHS,
    CHEWS_PER_DAY: CHEWS_PER_DAY,
    PUPPY_MAX_MONTHS: PUPPY_MAX_MONTHS,
    SCORE_KEYS: SCORE_KEYS,
    SCORE_CLUSTERS: SCORE_CLUSTERS,
    scoreClusters: scoreClusters,
    topConcern: topConcern,
    APPROX_YEAR_OFFSET_MONTHS: APPROX_YEAR_OFFSET_MONTHS,
    DOG_NAME_MAX: DOG_NAME_MAX,
    OWNER_NAME_MAX: OWNER_NAME_MAX,
    cleanOwnerName: cleanOwnerName,
    utc: utc,
    startOfMonth: startOfMonth,
    addMonths: addMonths,
    subMonths: subMonths,
    monthsBetween: monthsBetween,
    nextAnniversary: nextAnniversary,
    nextAnniversaryMonth: nextAnniversaryMonth,
    toISODate: toISODate,
    deriveDob: deriveDob,
    shouldAskActivity: shouldAskActivity,
    computeProfile: computeProfile,
    buildProperties: buildProperties
  };
}));
